import { FC, useState, useCallback } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { LightTooltip } from "@/components/LightTooltip";
import { useIntentFiltersStorage } from "./hooks/useIntentFiltersStorage";

/**
 * Menu component for Intent Topics display preferences
 * Shows options for filtering topics (all vs technology-related)
 */
export const IntentTopicsMenu: FC = () => {
  const { intentFilters, setIntentFilters } = useIntentFiltersStorage();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const open = Boolean(anchorEl);
  const currentValue = intentFilters.techTopicsOnly ? "tech" : "all";

  const handleOpen = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    },
    []
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      setIntentFilters((prev) => ({
        ...prev,
        techTopicsOnly: newValue === "tech",
      }));
      handleClose();
    },
    [setIntentFilters, handleClose]
  );

  return (
    <>
      <IconButton size="small" onClick={handleOpen} sx={{ p: 0.5 }}>
        <MoreVertIcon sx={{ fontSize: 20 }} />
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Box sx={{ width: 320, p: 2 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Typography variant="subtitle1" fontWeight={500}>
              Intent Topics
            </Typography>
            <IconButton size="small" onClick={handleClose} sx={{ p: 0.25 }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Stack>

          <Typography variant="body2" color="text.secondary" mb={2}>
            Select your display preferences for the Intent table below:
          </Typography>

          <RadioGroup value={currentValue} onChange={handleChange}>
            <FormControlLabel
              value="tech"
              control={<Radio size="small" sx={{ p: 0, mr: 1 }} />}
              label={
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="body2">
                    Show all technology related topics
                  </Typography>
                  <LightTooltip
                    title="Technology related topics are topics mapped to Product Installs, Categories or Vendors"
                    placement="right"
                  >
                    <InfoOutlinedIcon
                      sx={{ fontSize: 16, color: "text.secondary" }}
                    />
                  </LightTooltip>
                </Stack>
              }
              sx={{ m: 0 }}
            />
            <FormControlLabel
              value="all"
              control={<Radio size="small" sx={{ p: 0, mr: 1 }} />}
              label={<Typography variant="body2">Show all topics</Typography>}
              sx={{ m: 0, mt: 1 }}
            />
          </RadioGroup>
        </Box>
      </Popover>
    </>
  );
};
